<template>
    <div class="page-wrap">
        <!-- Title and tabs stick together; separately, both page-bars would stick at top:0 and overlap -->
        <div class="page-bar-sticky">
            <div class="page-bar">
                <div>
                    <div class="page-bar-sub">Foundations</div>
                    <div class="page-bar-main">Review</div>
                </div>
            </div>
            <div class="page-bar page-bar-toggles-wrap">
                <div class="page-bar-toggles">
                    <div v-for="(type, i) in types" :key="i" @click="selectType(type.type)" :class="selectedType == type.type ? 'page-bar-toggle-s' : ''">
                        {{ type.type }}<span>{{ type.count }}</span>
                    </div>
                </div>
            </div>
        </div>
        <div :class="$store.state.routeLoaded ? 'slide-up view' : 'hide'">
            <div class="page-filters pf-bar" v-if="$store.state.user.isAdmin">
                <div class="pf-search">
                    <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.35-4.35" /></svg>
                    <input type="text" v-model="searchQ" placeholder="Search" aria-label="Search" @input="search()" />
                </div>
                <template v-if="selectedType == 'Process Suggestions'">
                    <span class="pf-spacer"></span>
                    <div @click="() => { showAddSuggestion = true }" class="add-btn">Add</div>
                </template>
            </div>

            <!-- Draft / Needs Approval / Inactive -->
            <table v-if="isNodeType" class="full-table">
                <tbody>
                    <tr class="table-labels">
                        <td>Name</td>
                        <td>Last Modified</td>
                        <td>Modifed By</td>
                        <td>Type</td>
                        <td>Process Owner</td>
                        <td></td>
                        <td></td>
                    </tr>
                    <tr style="height:0;padding:0"></tr>
                    <tr v-for="(node, i) in nodes" :key="i">
                        <td>
                            <router-link :to="'/playbook/' + node.Slug">{{ node.Name }}</router-link>
                        </td>
                        <td>{{ node.LastModifiedString }}</td>
                        <td>{{ node.LastUpdatedBy }}</td>
                        <td>{{ $store.state.playbookNodeTypeMap[node.Type] }}</td>
                        <td>{{ node.ProcessOwnerNames }}</td>
                        <td v-if="node.Type == 'PROCESS'" style="text-align:center">
                            <router-link :to="'/edit-process/' + node.Slug">Edit</router-link>
                        </td>
                        <td v-else-if="node.Type == 'TIMELINE'" style="text-align:center">
                            <router-link :to="'/edit-timeline/' + node.Slug">Edit</router-link>
                        </td>
                        <td v-else-if="node.Type == 'SHEET'" style="text-align:center">
                            <router-link :to="'/edit-sheet/' + node.Slug">Edit</router-link>
                        </td>
                        <td v-else-if="node.Type == 'CHECKLIST'" style="text-align:center">
                            <router-link :to="'/edit-checklist/' + node.Slug">Edit</router-link>
                        </td>
                        <td v-else></td>
                        <td style="text-align:center">
                            <a @click.prevent="deleteNode(node)">Delete</a>
                        </td>
                    </tr>
                </tbody>
            </table>

            <table v-else-if="selectedType == 'Needs Review'" class="full-table">
                <tbody>
                    <tr class="table-labels">
                        <td>Name</td>
                        <td>Modifed By</td>
                        <td>Type</td>
                        <td>Process Owner</td>
                        <td>Age</td>
                    </tr>
                    <tr style="padding:0;height:0"></tr>
                    <tr v-for="(node, i) in reviewNodes" :key="i">
                        <td>
                            <router-link :to="'/playbook/' + node.Slug">{{ node.Name }}</router-link>
                        </td>
                        <td>{{ node.LastUpdatedBy }}</td>
                        <td>{{ $store.state.playbookNodeTypeMap[node.Type] }}</td>
                        <td>{{ node.ProcessOwnerNames }}</td>
                        <td>{{ node.AgeString }}</td>
                    </tr>
                </tbody>
            </table>

            <table v-else-if="selectedType == 'Sign Offs'" class="full-table">
                <tbody>
                    <tr class="table-labels">
                        <td>Date</td>
                        <td>Process</td>
                        <td>Signature</td>
                        <td>Sheet Column</td>
                    </tr>
                    <tr style="padding:0;height:0"></tr>
                    <tr v-for="(so, i) in signOffs" :key="i">
                        <td>{{ so.DateDisplay }}</td>
                        <td><router-link :to="'/playbook/' + so.Slug">{{ so.NodeName }}</router-link></td>
                        <td>
                            <a v-if="so.SignatureData" @click.prevent="showSignOffCells(so)">{{ so.Signature }}</a>
                            <span v-else>{{ so.Signature }}</span>
                        </td>
                        <td><a v-if="so.Seat" @click.prevent="showSignOffCells(so)">{{ so.SeatName }}</a></td>
                    </tr>
                </tbody>
            </table>

            <table v-else-if="selectedType == 'Process Suggestions'" class="full-table">
                <tbody>
                    <tr class="table-labels">
                        <td>Date</td>
                        <td>User</td>
                        <td>Suggestion</td>
                        <td v-if="canForge"></td>
                    </tr>
                    <tr style="height:0;padding:0"></tr>
                    <tr v-for="(s, i) in suggestions" :key="i">
                        <td>{{ s.DateDisplay }}</td>
                        <td>{{ s.User }}</td>
                        <td><div v-html="s.Text"></div></td>
                        <!-- The Forge drafts the change into the process (ForgeSuggestionDraft, API routes/ai.ts /suggestion-draft) -->
                        <td v-if="canForge" style="text-align:center;white-space:nowrap">
                            <span v-if="s.ForgeStatus == 'saved'" class="na-forge-done">Saved as draft</span>
                            <a v-else-if="s.ID" @click.prevent="forgeSuggestion = s">{{ s.ForgeStatus == 'drafted' ? 'Open the Forge draft' : 'Draft with the Forge' }}</a>
                        </td>
                    </tr>
                </tbody>
            </table>

            <table v-else-if="selectedType == 'Comments'" class="full-table">
                <tbody>
                    <tr class="table-labels">
                        <td>Date</td>
                        <td>Process</td>
                        <td>User</td>
                        <td>Comment</td>
                    </tr>
                    <tr style="padding:0;height:0"></tr>
                    <tr v-for="(c, i) in comments" :key="i">
                        <td>{{ c.DateDisplay }}</td>
                        <td><router-link :to="'/playbook/' + c.Slug">{{ c.Name }}</router-link></td>
                        <td>{{ c.User }}</td>
                        <td><div v-html="c.Comment"></div></td>
                    </tr>
                </tbody>
            </table>
        </div>
        <confirm-delete v-if="showConfirmDelete" @confirm="deleteNodeConfirm" @cancel="() => { showConfirmDelete = false; }" msg="Are you sure you would like to PERMANENTLY DELETE this process?"/>
        <sign-off-cells v-if="showSignOff" :signOff="signOffToShow" @close="() => { showSignOff = false }" />
        <forge-suggestion-draft v-if="forgeSuggestion" :suggestion="{ ID: forgeSuggestion.ID, ForgeStatus: forgeSuggestion.ForgeStatus }" @close="forgeSuggestion = null"
            @drafted="() => { forgeSuggestion.ForgeStatus = 'drafted'; }" @saved="() => { forgeSuggestion.ForgeStatus = 'saved'; forgeSuggestion = null; }" @dismissed="() => { forgeSuggestion.ForgeStatus = 'dismissed'; forgeSuggestion = null; }" />
        <process-suggestion-form v-if="showAddSuggestion" @close="async () => { showAddSuggestion = false; await getSuggestions(); search(); }" />
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import axios from 'axios';
import ConfirmDelete from '@/components/ConfirmDelete.vue';
import SignOffCells from '@/components/SignOffCells.vue';
import { readSeatSignOff, seatLabel } from '@/roleSheet';
import ProcessSuggestionForm from '@/components/ProcessSuggestionForm.vue';
import ForgeSuggestionDraft from '@/components/ForgeSuggestionDraft.vue';

// Draft/Needs Approval/Inactive come from one list of nodes, split by NeedsApprovalType
const NODE_TYPES = ['Draft', 'Needs Approval', 'Inactive'];

// Review hub (/review): needs-approval nodes plus the tables that used to be the Needs Review,
// Sign Offs, Process Suggestions and Comments views. ?type= picks the tab
export default defineComponent({
    name: 'NeedsApproval',
    components: {
        ConfirmDelete,
        SignOffCells,
        ProcessSuggestionForm, ForgeSuggestionDraft,
    },
    data() {
        return {
            allNodes: [] as any,
            nodes: [] as any,
            allReviewNodes: [] as any,
            reviewNodes: [] as any,
            allSignOffs: [] as any,
            signOffs: [] as any,
            allSuggestions: [] as any,
            suggestions: [] as any,
            allComments: [] as any,
            comments: [] as any,
            searchQ: '',
            showConfirmDelete: false,
            nodeToDelete: null as any,
            showSignOff: false,
            signOffToShow: null as any,
            showAddSuggestion: false,
            forgeSuggestion: null as any, // the suggestion open in the Forge
            selectedType: 'Needs Review',
            types: [
                { type: 'Needs Review', count: 0 },
                { type: 'Draft', count: 0 },
                { type: 'Needs Approval', count: 0 },
                { type: 'Inactive', count: 0 },
                { type: 'Sign Offs', count: 0 },
                { type: 'Process Suggestions', count: 0 },
                { type: 'Comments', count: 0 },
            ]
        }
    },
    computed: {
        isNodeType(): boolean {
            return NODE_TYPES.includes(this.selectedType);
        },
        // The Forge's write tools are for Admins and Editors (API routes/ai.ts gate)
        canForge(): boolean {
            const u = this.$store.state.user || {};
            return !!u.isAdmin && [ 'Admin', 'Editor' ].includes(u.role);
        }
    },
    async created() {
        if(this.$route.query.search) {
            this.searchQ = this.$route.query.search.toLowerCase();
        }
        if(this.isValidType(this.$route.query.type)) {
            this.selectedType = this.$route.query.type;
        }
        await Promise.all([
            this.getNodes(),
            this.getReviewNodes(),
            this.getSignOffs(),
            this.getSuggestions(),
            this.getComments(),
        ]);
        this.search();

        this.$store.state.routeLoaded = true;
    },
    watch: {
        // Menu links to a specific tab while this view is already open
        '$route.query.type'(type) {
            if(type != this.selectedType && this.isValidType(type)) {
                this.selectedType = type;
                this.search();
            }
        }
    },
    methods: {
        isValidType(type: any): boolean {
            return this.types.some((t: any) => t.type == type);
        },
        setCount(type: string, count: number) {
            const t = this.types.find((t: any) => t.type == type);
            if(t) t.count = count;
        },
        // Number of rows whose Date is within the past 7 days
        countRecent(rows: any[]): number {
            const cutoff = new Date().getTime() - 7 * 24 * 60 * 60 * 1000;
            return rows.filter((r: any) => r.Date && new Date(r.Date).getTime() >= cutoff).length;
        },
        selectType(type: string) {
            this.selectedType = type;
            this.search();
        },
        async getNodes() {
            await axios.get(`${this.$store.state.apiUrl}/api/playbook/needsapprovalnodes`)
                .then(resp => {
                    const userID = this.$store.state.user.userID;
                    resp.data.forEach((p: any) => {
                        if(p.LastModified) {
                            p.LastModifiedString = new Date(p.LastModified).toLocaleDateString();
                        }
                        if((p.ProcessOwners || []).some((o: any) => o.UserID == userID)) {
                            p.Mine = true;
                        }
                    })
                    // Items the user owns are listed first
                    this.allNodes = [
                        ...resp.data.filter((p: any) => { return p.Mine }),
                        ...resp.data.filter((p: any) => { return !p.Mine }),
                    ];
                })
                .catch(e => {
                    console.log(e);
                })

            for(const type of NODE_TYPES) {
                this.setCount(type, this.allNodes.filter((n: any) => n.NeedsApprovalType == type).length);
            }
        },
        async getReviewNodes() {
            await axios.get(`${this.$store.state.apiUrl}/api/playbook/needsreviewnodes`)
                .then(resp => {
                    const userID = this.$store.state.user.userID;
                    resp.data.forEach((p: any) => {
                        if((p.ProcessOwners || []).some((o: any) => o.UserID == userID)) {
                            p.Mine = true;
                        }
                        if(p.LastReviewed) {
                            const ms = new Date().getTime() - new Date(p.LastReviewed).getTime();
                            const hours = ms / (1000 * 60 * 60);

                            if (hours > 24) {
                                const days = Math.round(hours / 24);
                                p.AgeString = `${days} day${days === 1 ? '' : 's'}`;
                            } else {
                                const roundedHours = Math.max(1, Math.round(hours));
                                p.AgeString = `${roundedHours} hour${roundedHours === 1 ? '' : 's'}`;
                            }
                        }
                    })
                    // Items the user owns are listed first
                    this.allReviewNodes = [
                        ...resp.data.filter((p: any) => { return p.Mine }),
                        ...resp.data.filter((p: any) => { return !p.Mine }),
                    ];
                })
                .catch(e => {
                    console.log(e);
                })
            this.setCount('Needs Review', this.allReviewNodes.length);
        },
        async getSignOffs() {
            await axios.get(`${this.$store.state.apiUrl}/api/signoffs`)
                .then(resp => {
                    for(const so of resp.data) {
                        so.DateDisplay = new Date(so.Date).toLocaleDateString();
                        so.Seat = readSeatSignOff(so);
                        so.SeatName = so.Seat ? seatLabel(so.Seat) || 'Seat' : '';
                    }
                    this.allSignOffs = resp.data;
                })
                .catch(e => {
                    console.log(e);
                })
            this.setCount('Sign Offs', this.countRecent(this.allSignOffs));
        },
        async getSuggestions() {
            await axios.get(`${this.$store.state.apiUrl}/api/processsuggestions`)
                .then(resp => {
                    for(const s of resp.data) {
                        if(s.Date) {
                            s.DateDisplay = new Date(s.Date).toLocaleDateString();
                        }
                    }
                    this.allSuggestions = resp.data;
                })
                .catch(e => {
                    console.log(e);
                })
            this.setCount('Process Suggestions', this.countRecent(this.allSuggestions));
        },
        async getComments() {
            await axios.get(`${this.$store.state.apiUrl}/api/playbook/allcomments`)
                .then(resp => {
                    for(const c of resp.data) {
                        if(c.Date) {
                            c.DateDisplay = new Date(c.Date).toLocaleDateString();
                        }
                    }
                    this.allComments = resp.data;
                })
                .catch(e => {
                    console.log(e);
                })
            this.setCount('Comments', this.countRecent(this.allComments));
        },
        // Filters every tab's list; the search box applies to whichever tab is showing
        search() {
            const q = this.searchQ && this.searchQ.length >= 3 ? this.searchQ.toLowerCase() : '';
            const has = (value: any) => (value || '').toLowerCase().includes(q);

            this.nodes = this.allNodes.filter((p: any) => p.NeedsApprovalType == this.selectedType && (!q || has(p.Name)));
            this.reviewNodes = this.allReviewNodes.filter((p: any) => !q || has(p.Name));
            this.signOffs = this.allSignOffs.filter((p: any) => !q || has(p.NodeName) || has(p.Signature));
            this.suggestions = this.allSuggestions.filter((s: any) => !q || has(s.User));
            this.comments = this.allComments.filter((c: any) => !q || has(c.User) || has(c.Name));

            const query = {
                type: this.selectedType
            } as any;
            if(q) {
                query.search = q;
            }
            this.$router.replace({
                path: this.$route.path,
                query: query
            }).catch((e: any) => {
                console.log(e);
            });
        },
        async deleteNodeConfirm() {
            await axios.post(`${this.$store.state.apiUrl}/api/playbook/deletenode`, this.nodeToDelete)
                .catch(e => {
                    console.log(e?.response?.data || e);
                    alert('There was an error')
                })

            await this.getNodes();
            this.search();

            this.showConfirmDelete = false;
        },
        async deleteNode(process: any) {
            this.nodeToDelete = process;
            this.showConfirmDelete = true;
        },
        showSignOffCells(signOff: any) {
            this.signOffToShow = signOff;
            this.showSignOff = true;
        }
    }
});
</script>

<style scoped>
.na-forge-done { font-size: 12.5px; color: #2c7a4b; font-weight: 600; }
</style>
